import { useMutation, useQuery } from '@tanstack/react-query';
import { LinearOptimizationsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { download } from '../../utils/download-utils';
import {
  RawConstraintDebuggerRow,
  transformConstraintDebuggerRow
} from '../../utils/optimization-utils';
import { useSetError } from '../../redux/modals';

export interface RunResultRow {
  Network: string;
  Avail: string;
  'Spot Length': string;
  Day: string;
  Daypart: string;
  'Daypart Name': string;
  Secured: string;
  'Number of Spots': string;
  Cost: string;
  Creative: string;
  [key: string]: string;
}

// WNAT (satellite/national avail) rows with 20 or fewer spots are dropped; 21-33 round up to the
// minimum buyable unit of 34.
const applyWnatSpotFloor = (rows: RunResultRow[]): RunResultRow[] =>
  rows
    .map(row => {
      if (row.Network !== 'WNAT') return row;
      const spots = parseFloat(row['Number of Spots']);
      if (spots <= 20) return null;
      if (spots < 34) return { ...row, 'Number of Spots': '34' };
      return row;
    })
    .filter((row): row is RunResultRow => row !== null);

export const useRunResult = (kpi: string, runId: string) =>
  useQuery({
    queryKey: ['linearRunResult', kpi, runId],
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/optimizations', {
        params: { kpi, runId, csv: true }
      });
      const rows = await awaitJSON<RunResultRow[]>(result);
      return applyWnatSpotFloor(rows);
    },
    enabled: Boolean(kpi && runId)
  });

// On-demand only -- triggered by the Constraint Debugger tab's "Download constraint data" button,
// not fetched automatically on tab load.
export const useConstraintDebuggerData = () => {
  const setError = useSetError();
  return useMutation({
    mutationFn: async ({
      branch,
      kpi,
      runId,
      saveLocally
    }: {
      branch: string;
      kpi: string;
      runId: string;
      saveLocally: boolean;
    }) => {
      const result = await LinearOptimizationsLambdaFetch('/constraintsDebugger', {
        params: { branch, kpi, runId }
      });
      const { csv, data } = await awaitJSON<{ csv: string; data: RawConstraintDebuggerRow[] }>(
        result
      );
      if (saveLocally) {
        download(csv, `Constraints: ${branch}/${kpi}/${runId}.csv`, 'text/csv');
      }
      return data.map(transformConstraintDebuggerRow);
    },
    onError: error =>
      setError({ message: `Couldn't fetch constraint data: ${error.message}`, reportError: error })
  });
};
